import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Users, TrendingUp, Heart, Zap } from 'lucide-react';
import { groupInfo, leadershipTeam } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const About = () => {
  const values = [
    {
      icon: Users,
      title: "Collective Empowerment",
      description: "Building strength through unity and mutual support"
    },
    {
      icon: TrendingUp,
      title: "Financial Independence",
      description: "Building the foundation for confident financial decision-making and family prosperity."
    },
    {
      icon: Heart,
      title: "Community Care",
      description: "Fostering a supportive and inclusive environment"
    },
    {
      icon: Zap,
      title: "Innovation",
      description: "Embracing technology for better management and transparency"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-50 via-white to-purple-50 py-8 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl md:text-6xl font-bold text-purple-900 mb-6">About Us</h1>
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              {groupInfo.aboutus}
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Mission */}
            <Card className="border-purple-100 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="bg-purple-100 p-4 rounded-full">
                    <Target className="text-purple-700" size={32} />
                  </div>
                  <h2 className="text-3xl font-bold text-purple-900">Our Mission</h2>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {groupInfo.mission}
                </p>
              </CardContent>
            </Card>

            {/* Vision */}
            <Card className="border-purple-100 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="bg-purple-100 p-4 rounded-full">
                    <Eye className="text-purple-700" size={32} />
                  </div>
                  <h2 className="text-3xl font-bold text-purple-900">Our Vision</h2>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {groupInfo.vision}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-gradient-to-b from-purple-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-purple-900 mb-4">Our Core Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card 
                  key={index}
                  className="group text-center hover:shadow-xl transition-all duration-300 border-purple-100 hover:border-purple-300"
                >
                  <CardContent className="p-8">
                    <div className="bg-purple-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-purple-600 transition-colors duration-300">
                      <IconComponent className="text-purple-700 group-hover:text-white transition-colors duration-300" size={36} />
                    </div>
                    <h3 className="text-xl font-bold text-purple-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-purple-900 mb-4">Meet Our Leadership</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Dedicated leaders committed to the growth and empowerment of our members
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {leadershipTeam.map((member) => (
              <Card 
                key={member.id} 
                className="group hover:shadow-xl transition-all duration-300 border-purple-100 hover:border-purple-300"
              >
                <CardContent className="p-6 text-center">
                  <div className="relative mb-4 mx-auto w-32 h-32">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full rounded-full object-cover ring-4 ring-purple-100 group-hover:ring-purple-300 transition-all duration-300"
                    />
                    <div className="absolute inset-0 rounded-full bg-purple-600 opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                  </div>
                  <h3 className="text-xl font-bold text-purple-900 mb-1">{member.name}</h3>
                  <p className="text-purple-600 font-semibold mb-3">{member.position}</p>
                  <p className="text-gray-600 text-sm leading-relaxed">{member.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-purple-600 to-purple-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Join Our Journey</h2>
            <p className="text-xl text-purple-100 mb-8 leading-relaxed">
              Be part of a community that's transforming lives through empowerment, innovation, and collective action
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button className="bg-white text-purple-700 hover:bg-purple-50 px-8 py-6 text-lg rounded-full transition-all duration-200 shadow-lg hover:shadow-xl">
                  Get in Touch
                </Button>
              </Link>
              <Link to="/news-events">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-full transition-all duration-200">
                  View Our Activities
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
